// page-meetings-records-guides.ts — Vue « Guides » de #meetings-records.
//
// Troisième mode d'affichage, à côté de Liste et Calendrier. Une ligne par
// réunion, une colonne par guide du référentiel : l'animateur voit d'un coup
// d'œil ce qui reste à traiter sur son mois. Les cinq colonnes d'identité
// (n° → date) sont figées, les guides défilent à leur droite.
//
// Le défilement du temps (◀ mois ▶ Aujourd'hui) et le chargement mensuel sont
// ceux des deux autres vues — la page les porte, cette vue s'y branche.
//
// Rendu PUR : aucun état de closure, tout arrive en paramètre. `esc` et `t`
// sont des globales (cf. types/core-globals.d.ts).

import {
  cellFor,
  sortGrid,
  type GridCell,
  type GridMeeting,
  type GridSortKey,
  type StatusByDefinition,
} from '../domain/meeting-guides-grid.js';
import {
  committeeCell,
  programProjectCell,
  type ProgramRef,
} from './page-meetings-records-rows.js';

export interface GuideDefinition {
  id: number;
  name: string;
  description: string;
  phase: string;
}

export interface GuidesOverview {
  definitions: GuideDefinition[];
  meetings: Record<string, StatusByDefinition>;
}

/** Pictos et couleurs repris de la fiche réunion : même langage visuel d'un
 *  écran à l'autre (cf. page-meeting-guides.ts). */
const CELL_ICON: Record<GridCell, string> = {
  pending: '○',
  done: '✔',
  skipped: '✖',
  absent: '·',
};
const CELL_COLOR: Record<GridCell, string> = {
  pending: 'var(--text3)',
  done: 'var(--accent)',
  skipped: 'var(--danger)',
  absent: 'var(--border2)',
};

function cellTitle(cell: GridCell): string {
  return cell === 'absent'
    ? t('pageMeetingsRecords.guidesNotApplicable')
    : t('pageMeeting.guides.status.' + cell);
}

function formatDate(iso: string, time: string | null): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}` + (time ? ` ${time.slice(0, 5)}` : '');
}

/**
 * Colonnes figées : l'identité de la réunion (n° → date) reste visible quand on
 * fait défiler les guides vers la droite. Sans ça, on perd de vue à quelle
 * réunion appartient la case qu'on regarde — le tableau devient illisible dès
 * qu'un tenant dépasse une demi-douzaine de guides.
 *
 * Le rendu ne pose que la classe : `position:sticky` n'empile pas les colonnes
 * tout seul, il faut un `left` cumulé, et les largeurs réelles ne se devinent
 * pas au rendu — une largeur supposée laisse des trous par où le contenu qui
 * défile transparaît. `applyStickyOffsets` les mesure après insertion.
 */
/** Pose le décalage de chaque colonne figée, mesuré sur le tableau rendu. */
export function applyStickyOffsets(root: ParentNode): void {
  const head = root.querySelectorAll<HTMLElement>('#mrg-table thead .mrg-sticky');
  let offset = 0;
  const offsets: number[] = [];
  head.forEach((th) => {
    offsets.push(offset);
    offset += th.getBoundingClientRect().width;
  });
  root.querySelectorAll<HTMLElement>('#mrg-table tr').forEach((tr) => {
    const cells = tr.querySelectorAll<HTMLElement>('.mrg-sticky');
    cells.forEach((cell, i) => {
      // Arrondi vers le bas : la colonne recouvre alors sa voisine d'une
      // fraction de pixel au lieu de lui laisser un jour.
      cell.style.left = Math.floor(offsets[i] ?? 0) + 'px';
      cell.classList.toggle('mrg-sticky-last', i === cells.length - 1);
    });
  });
}

function sortableTh(
  key: GridSortKey,
  label: string,
  sortKey: GridSortKey,
  sortDir: 'asc' | 'desc',
  extra = '',
  title = '',
  sticky = false,
): string {
  const active = sortKey === key;
  const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
  const t2 = title ? ` title="${esc(title)}"` : '';
  const cls = 'cursor-pointer' + (active ? ' sorted' : '') + (sticky ? ' mrg-sticky' : '');
  return `<th class="${cls}" data-sort-mrg="${key}"${
    extra ? ` style="${extra}"` : ''
  }${t2}>${esc(label)} <span class="text-xs">${arrow}</span></th>`;
}

/**
 * Tableau complet de la vue. Exporté pour être testé sans DOM.
 *
 * `meetings` a déjà été restreint par le serveur (réunions animées, ou toutes
 * pour un Directeur / Superviseur) puis par la barre de filtres commune aux
 * trois vues.
 */
export function renderGuidesGrid(
  meetings: readonly GridMeeting[],
  overview: GuidesOverview,
  programById: Map<number, ProgramRef>,
  sortKey: GridSortKey,
  sortDir: 'asc' | 'desc',
): string {
  if (!overview.definitions.length) {
    return `<div class="card"><div class="empty">${esc(t('pageMeetingsRecords.guidesNoDefinitions'))}</div></div>`;
  }

  const head =
    sortableTh('id', t('pageMeetingsRecords.colNum'), sortKey, sortDir, 'width:48px', '', true) +
    sortableTh(
      'label',
      t('pageMeetingsRecords.colLabel'),
      sortKey,
      sortDir,
      'width:220px',
      '',
      true,
    ) +
    sortableTh(
      'project_code',
      t('pageMeetingsRecords.colProgramProject'),
      sortKey,
      sortDir,
      'width:160px',
      '',
      true,
    ) +
    sortableTh(
      'committee_code',
      t('pageMeetingsRecords.colCommittee'),
      sortKey,
      sortDir,
      'width:100px',
      '',
      true,
    ) +
    sortableTh(
      'meeting_date',
      t('pageMeetingsRecords.colDate'),
      sortKey,
      sortDir,
      'width:120px',
      '',
      true,
    ) +
    overview.definitions
      .map((d) =>
        // Libellé complet en infobulle : une colonne par guide, les en-têtes
        // doivent rester étroites sinon le tableau part en largeur.
        sortableTh(
          `guide:${d.id}`,
          d.name,
          sortKey,
          sortDir,
          'text-align:center;white-space:normal;min-width:88px',
          d.description || d.name,
        ),
      )
      .join('');

  const rows = sortGrid(meetings, overview.meetings, sortKey, sortDir)
    .map((m) => {
      const statuses = overview.meetings[String(m.id)];
      const cells = overview.definitions
        .map((d) => {
          const cell = cellFor(statuses, d.id);
          const label = cellTitle(cell);
          return `<td style="text-align:center;color:${CELL_COLOR[cell]}"
              title="${esc(label)}" aria-label="${esc(label)}">${CELL_ICON[cell]}</td>`;
        })
        .join('');
      return `<tr>
          <td class="mrg-sticky cursor-pointer text-mono text-sm font-bold" style="text-decoration:underline" data-mr-edit="${m.id}">${m.id}</td>
          <td class="mrg-sticky cursor-pointer truncate" data-mr-edit="${m.id}" style="color:var(--text);font-weight:500;max-width:220px" title="${esc(m.label)}">${esc(m.label)}</td>
          <td class="mrg-sticky" style="white-space:nowrap">${programProjectCell(m, programById)}</td>
          <td class="mrg-sticky" style="white-space:nowrap">${committeeCell(m)}</td>
          <td class="mrg-sticky" style="white-space:nowrap">${esc(m.meeting_date ? formatDate(m.meeting_date, m.meeting_time) : '—')}</td>
          ${cells}
        </tr>`;
    })
    .join('');

  const body =
    rows ||
    `<tr><td colspan="${5 + overview.definitions.length}" class="text-center text-muted" style="padding:24px">${esc(
      t('pageMeetingsRecords.guidesEmpty'),
    )}</td></tr>`;

  return `<div class="card">
      <div class="table-wrap">
        <table class="table" id="mrg-table">
          <thead><tr>${head}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>`;
}
